import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getMajorBook } from '../../../../../../lib/api/accounting';
import GlobalSearcher, {
  Filter,
} from '../../../../../../lib/common/GlobalSearcher';
import CustomButton from '../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions from '../../../../../../lib/designSystem/HeaderWithActions';
import { fetchWithDateChunking } from '../utils/fetchWithDateChunking';
import {
  deepMergeGroupedData,
  mergeMajorBookAccounts,
} from '../utils/mergeStrategies';
import MajorBookEntriesTable from './components';

dayjs.extend(utc);

import ExcelJS from 'exceljs';
import { formatDateToLocal } from 'lib/designSystem/Table/cellFormat';
import { formatDate, showAlert } from 'lib/functions/utils';
import { useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

interface responseType {
  majorBookAccounts: {
    groupedLines: {
      date: string;
      businessIdentifier: string;
      businessName: string;
    }[];
  }[];
  groupedByCategoryAndBusiness?: any;
}

const exportToExcel = async (
  entries: responseType,
  startDate?: Date,
  endDate?: Date,
  businessIdentifier?: string,
  businessName?: string,
  accountCode?: string,
) => {
  if (!entries?.majorBookAccounts) return;
  // Mapeo de datos planos
  const plainData = entries.majorBookAccounts.flatMap((category: any) => {
    return category.groupedLines.map((line: any) => {
      return [
        line.date, // Fecha
        line.businessIdentifier, // Rut cliente
        line.businessName, // Nombre cliente
        line.account, // Cuenta
        line.accountName, // Nombre de Cuenta
        line.description, // Descripción
        line.entryId, // ID Asiento
        line.className, // Clase Asiento
        line.documentId, // Documento
        line.debit, // Débito
        line.credit, // Crédito
        line.debitFinal, // Débito final
        line.creditFinal, // Crédito final
      ];
    });
  });

  // Encabezados de la hoja de Excel
  const headers = [
    'Fecha',
    'Rut cliente',
    'Nombre cliente',
    'Cuenta',
    'Nombre de Cuenta',
    'Descripción',
    'ID Asiento',
    'Clase Asiento',
    'Documento',
    'Debe',
    'Haber',
    'Débito',
    'Crédito',
  ];

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Sheet1');

  const infoRows = [
    ['Informacion de la consulta', ''],
    ['Razón social:', businessName || 'N/A'],
    ['RUT:', businessIdentifier || 'N/A'],
    ['Tipo de reporte:', 'Libro Mayor'],
    [
      'Periodo:',
      startDate && endDate
        ? `${formatDate(startDate)} a ${formatDate(endDate)}`
        : `Cuenta: ${accountCode}`,
    ],
    ['Fecha de consulta:', formatDate(new Date())],
    [], // Fila vacía como separador
  ];

  // Agregar filas de información
  infoRows.forEach((rowData) => {
    const row = worksheet.addRow(rowData);
    // Formato para las filas de información (primera columna en negrita)
    if (rowData.length > 0) {
      const firstCell = row.getCell(1);
      firstCell.font = { bold: true };
    }
  });

  // Agregar datos a la hoja
  [headers, ...plainData].forEach((rowData, rowIndex) => {
    const row = worksheet.addRow(rowData);

    if (rowIndex === 0) {
      // Formato para la primera fila (encabezados)
      row.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        worksheet.getColumn(cell.col).width = 20; // Ajustar ancho de columna
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFDDDDDD' }, // Color gris claro
        };
      });
    }
  });

  // Guardar el archivo
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);

  // Descargar el archivo
  const a = document.createElement('a');
  a.href = url;
  a.download =
    startDate && endDate
      ? `Libro Mayor ${formatDateToLocal(startDate)} - ${formatDateToLocal(endDate)}.xlsx`
      : `Libro Mayor ${accountCode}.xlsx`;
  a.click();

  URL.revokeObjectURL(url);
};

const MajorBook: React.FC<{
  transactional?: boolean;
  startDate?: Date;
  endDate?: Date;
}> = ({
  transactional = false,
  startDate: propStartDate,
  endDate: propEndDate,
}) => {
  const [data, setData] = useState<responseType>({
    majorBookAccounts: [],
    groupedByCategoryAndBusiness: {},
  });
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  // Initialize with current month
  const now = dayjs();
  const startDateDefault = now.startOf('month').toDate();
  const endDateDefault = now.endOf('month').toDate();
  const [accountCode, setAccountCode] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string | null>(
    propStartDate
      ? propStartDate.toISOString().split('T')[0]
      : startDateDefault.toISOString().split('T')[0],
  );
  const [endDate, setEndDate] = useState<string | null>(
    propEndDate
      ? propEndDate.toISOString().split('T')[0]
      : endDateDefault.toISOString().split('T')[0],
  );

  const triggerSearch = async () => {
    await setUp(startDate || undefined, endDate || undefined);
  };

  const setUp = async (
    startDateParam?: string,
    endDateParam?: string,
    showLoading: boolean = true,
  ) => {
    try {
      if (showLoading) setLoading(true);
      if (accountCode) {
        let queryParams = `accountCode=${accountCode}`;
        if (startDateParam) queryParams += `&startDate=${startDateParam}`;
        if (endDateParam) queryParams += `&endDate=${endDateParam}`;
        const result = await getMajorBook(dispatch, queryParams);
        setData(result);
        if (showLoading) setLoading(false);
        return;
      }
      if (startDateParam && endDateParam) {
        const result = await fetchWithDateChunking({
          startDate: startDateParam || '',
          endDate: endDateParam || '',
          thresholdMonths: 6,
          apiFunction: getMajorBook,
          mergeStrategy: (responses) => {
            const allMajorBookAccounts = responses.map(
              (response) => response?.majorBookAccounts || [],
            );
            const allGroupedResponses = responses.map(
              (response) => response?.groupedByCategoryAndBusiness || {},
            );

            const mergedMajorBookAccounts =
              mergeMajorBookAccounts(allMajorBookAccounts);
            const sortedMergedMajorBookAccounts = mergedMajorBookAccounts.sort(
              (a, b) => a.account.localeCompare(b.account),
            );

            return {
              majorBookAccounts: sortedMergedMajorBookAccounts,
              groupedByCategoryAndBusiness:
                deepMergeGroupedData(allGroupedResponses),
            };
          },
          additionalParams: { transactional, accountCode: accountCode || '' },
          dispatch,
        });
        setData(result);
      }
    } catch (error) {
      console.error('Error fetching major book:', error);
    }
    if (showLoading) setLoading(false);
  };

  const handleExport = async () => {
    if ((!startDate || !endDate) && !accountCode)
      return showAlert(
        dispatch,
        'error',
        'Fecha de inicio y fin son requeridas o el código de cuenta contable es requerido',
      );
    void exportToExcel(
      data,
      startDate ? new Date(startDate) : undefined,
      endDate ? new Date(endDate) : undefined,
      user?.businessIdentifier,
      user?.companyName,
      accountCode || undefined,
    );
  };

  useEffect(() => {
    if (propStartDate && propEndDate) {
      // Use props if available
      const startDateString = propStartDate.toISOString().split('T')[0];
      const endDateString = propEndDate.toISOString().split('T')[0];
      void setUp(startDateString, endDateString);
    } else {
      // Use default dates for initial load
      void setUp(
        formatDateToLocal(startDateDefault).split('T')[0],
        formatDateToLocal(endDateDefault).split('T')[0],
      );
    }
  }, [propStartDate, propEndDate]);

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <div>
        <HeaderWithActions
          title={`Período: ${startDate ? dayjs(startDate).format('DD/MM/YYYY') : ''} - ${endDate ? dayjs(endDate).format('DD/MM/YYYY') : ''}`}
          variant="body1"
          item={
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}
            >
              <div>
                <GlobalSearcher
                  filters={
                    [
                      {
                        type: 'date',
                        label: 'Fecha de inicio',
                        dateKey: 'startDate',
                        value: startDate,
                        setter: setStartDate,
                      },
                      {
                        type: 'date',
                        label: 'Fecha de fin',
                        dateKey: 'endDate',
                        value: endDate,
                        setter: setEndDate,
                      },
                      ...(!transactional
                        ? [
                            {
                              type: 'text',
                              label: 'Código cuenta contable',
                              value: accountCode,
                              setter: setAccountCode,
                            },
                          ]
                        : []),
                    ] as Filter[]
                  }
                  triggerSearch={triggerSearch}
                  shortcuts={true}
                />
              </div>
              <div>
                <CustomButton color="primary" onClick={handleExport}>
                  Exportar
                </CustomButton>
              </div>
            </div>
          }
        />
      </div>
      <div style={{ marginTop: 20 }}>
        <MajorBookEntriesTable
          entries={data}
          refreshData={async (showLoading = true) => {
            await setUp(
              startDate || undefined,
              endDate || undefined,
              showLoading,
            );
          }}
        />
      </div>
    </div>
  );
};

export default MajorBook;
